import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Home } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';

// 라우트 경로와 표시 이름 매핑
const routeNameMap: { [key: string]: string } = {
  '/': '홈',
  '/dashboard': '대시보드',
  '/survey-list': '설문 목록',
  '/survey-builder': '설문 생성',
  '/templates': '템플릿',
  '/create': '새 설문 만들기',
  '/edit': '설문 수정',
  '/survey-responses': '응답 관리',
  '/analytics': '통계 분석',
  '/statistics': '통계',
  '/user-management': '사용자 관리',
  '/management': '관리',
  '/management/survey-types': '설문 유형 관리',
  '/survey': '설문 응답',
};

interface BreadcrumbItem {
  path: string;
  name: string;
  isLast: boolean;
  isHome?: boolean;
}

export function AppBreadcrumb() {
  const location = useLocation();
  const params = useParams();
  
  // 경로를 세그먼트로 분할
  const pathSegments = location.pathname.split('/').filter(segment => segment);
  
  // Breadcrumb 아이템 생성
  const breadcrumbItems: BreadcrumbItem[] = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    
    // 파라미터가 있는 경우 처리 (예: /edit/:id)
    let displayName = routeNameMap[path] || segment;
    
    // ID 파라미터인 경우 특별 처리
    if (params.id && segment === params.id) {
      const parentPath = '/' + pathSegments.slice(0, index).join('/');
      if (parentPath === '/edit') {
        displayName = `설문 #${segment.substring(0, 8)}...`;
      } else if (parentPath === '/analytics') {
        displayName = `분석 #${segment.substring(0, 8)}...`;
      } else if (parentPath === '/survey') {
        displayName = `응답 #${segment.substring(0, 8)}...`;
      }
    }
    
    // management 하위 경로 처리
    if (path.startsWith('/management/')) {
      const subPath = path.replace('/management/', '');
      if (subPath === 'survey-types') {
        displayName = '설문 유형 관리';
      }
    }
    
    return {
      path,
      name: displayName,
      isLast: index === pathSegments.length - 1
    };
  });
  
  // 홈 아이템 추가
  const allItems: BreadcrumbItem[] = [
    { path: '/dashboard', name: '홈', isLast: pathSegments.length === 0, isHome: true },
    ...breadcrumbItems
  ];
  
  // 대시보드 페이지인 경우 breadcrumb 숨김
  if (location.pathname === '/dashboard' || location.pathname === '/') {
    return null;
  }
  
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {allItems.map((item, index) => (
          <React.Fragment key={item.path}>
            <BreadcrumbItem>
              {item.isLast ? (
                <BreadcrumbPage>
                  {item.isHome ? <Home className="h-4 w-4" /> : item.name}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={item.path}>
                    {item.isHome ? <Home className="h-4 w-4" /> : item.name}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < allItems.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}